import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Post } from '@/types'

import Link from 'next/link'

import { Seo, Layout } from '@/components/generals'

import { query } from '@/lib/db'

type BlogPost = Omit<Post, 'body' | 'published_on'> & { published_label: string }

interface BlogProps {
    posts: BlogPost[]
}

const Blog: FC<BlogProps> = ({ posts }): ReactNode => {
    const featured = posts.find((post) => post.featured) ?? posts[0]

    return (
        <Layout>
            <Seo title='Blog' description='News and resources from the frontiers of life science software and web applications.' />

            <div className='section'>
                <div className='container'>
                    <div className='blog-hero'>
                        <div className='center-title'>
                            <div className='title-holder'>
                                <div className='animate-on-load-01'>
                                    <h1 className='white-title'>Newsroom</h1>
                                </div>
                                <div className='animate-on-load-02'>
                                    <div className='experience-paragraph-holder'>
                                        <p>News and resources from the frontiers of life science software and web applications.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='prespective'>
                        <div className='animate-on-load-03'>
                            <div className='blog-post-main'>
                                <div className='w-dyn-list'>
                                    <div role='list' className='w-dyn-items'>
                                        {featured && (
                                            <div role='listitem' className='w-dyn-item'>
                                                <Link href={`/blog/${featured.slug}`} className='blog-post-holder w-inline-block'>
                                                    <div className='blog-post-container'>
                                                        <div className='blog-post-content'>
                                                            <div className='blog-content-holder'>
                                                                <div className='blog-category'>{featured.category}</div>
                                                                <div className='blog-title'>{featured.name}</div>
                                                                <p>{featured.summary}</p>
                                                            </div>
                                                            <p>{featured.published_label}</p>
                                                        </div>
                                                        <div className='blog-post-thumbnail-holder'><img src={featured.thumbnail_image ?? featured.main_image ?? ''} loading='lazy' alt={featured.name} className='blog-post-thumbnail' /></div>
                                                    </div>
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='section'>
                <div className='container'>
                    <div className='blog-cta-holder'>
                        <div className='blog-cta-container'>
                            <div className='blog-cta-email-holder'>
                                <div className='subscribe-text'>Subscribe to our newsletter for daily industry insights</div>
                                <div className='from-wrapper'>
                                    <div className='form-block w-form'>
                                        <form className='subscribe-form'>
                                            <div className='from-holder'><input className='text-field-form w-input' maxLength={256} name='email-2' placeholder='Enter Your Email' type='email' required /><input type='submit' className='button from w-button' value='Start Free Trial' /></div>
                                        </form>
                                        <div className='success-message w-form-done'>
                                            <div>Thank you! Your submission has been received!</div>
                                        </div>
                                        <div className='error-message w-form-fail'>
                                            <div>Oops! Something went wrong while submitting the form.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='follow-us-holder'>
                                <div className='subscribe-text'>Follow us</div>
                                <p>Get the latest news and travel inspiration.</p>
                                <div className='social-media-content'>
                                    <a href='https://linkedin.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-03.svg' loading='lazy' alt='Twitter' className='social-media-icon' /></a>
                                    <a href='https://instagram.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-02.svg' loading='lazy' alt='Facebook' className='social-media-icon' /></a>
                                    <a href='https://twitter.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-01.svg' loading='lazy' alt='Youtube' className='social-media-icon' /></a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='section'>
                <div className='container'>
                    <div className='blog-grid-wrapper w-dyn-list'>
                        <div role='list' className='blog-grid w-dyn-items'>
                            {posts.map((post) => (
                                <div key={post.id} role='listitem' className='blog-grid-item w-dyn-item'>
                                    <Link href={`/blog/${post.slug}`} className='blog-item-holder w-inline-block'>
                                        <div className='blog-item-thumbnail-holder'><img src={post.thumbnail_image ?? post.main_image ?? ''} loading='lazy' alt={post.name} className='blog-item-thumbnail' /></div>
                                        <div className='blog-content'>
                                            <div className='blog-category-holder'>
                                                <div className='blog-category'>{post.category}</div>
                                                <p className='no-margins'>•</p>
                                                <p className='no-margins'>{post.published_label}</p>
                                            </div>
                                            <div className='blog-small-name'>{post.name}</div>
                                            <div className='blog-summery-holder'>{post.summary}</div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<BlogProps> = async () => {
    const rows = await query<Post>('SELECT id, slug, name, summary, thumbnail_image, main_image, category, featured, published_on FROM posts ORDER BY featured DESC NULLS FIRST, published_on DESC NULLS LAST')

    const posts: BlogPost[] = rows.map((post) => ({
        id: post.id,
        slug: post.slug,
        name: post.name,
        summary: post.summary,
        thumbnail_image: post.thumbnail_image,
        main_image: post.main_image,
        category: post.category,
        featured: post.featured,
        published_label: post.published_on
            ? new Date(post.published_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : ''
    }))

    return { props: { posts } }
}

export default Blog
